'use client';

import { c as _c } from "react/compiler-runtime";
import { useRef, useState, useCallback, useEffect, type ReactNode } from 'react';
import Link from 'next/link';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
export interface FeedCarouselExploreMore {
  /** Link destination for the explore-more card */
  href: string;
  /** Primary label (e.g. "Explore more") */
  label: string;
  /** Secondary description text */
  description?: string;
}
export interface FeedCarouselProps {
  /** Items to render. Can be ReactNode[] (sync) or a render function that receives loading state. */
  children: ReactNode;
  /** Show navigation arrows for scrolling. Defaults to true. */
  showArrows?: boolean;
  /** Gap between items in pixels. Defaults to 16 (gap-4). */
  gap?: number;
  /** Horizontal padding class for the scroll container. Defaults to "px-5 sm:px-8 lg:px-12". */
  paddingClass?: string;
  /** Optional explore-more card shown at the end of the carousel. */
  exploreMore?: FeedCarouselExploreMore;
  /** Additional className for the outer wrapper */
  className?: string;
  /** Scroll amount per arrow click in pixels. Defaults to 360. */
  scrollAmount?: number;
}
export function FeedCarousel(t0) {
  const $ = _c(31);
  const {
    children,
    showArrows: t1,
    gap: t2,
    paddingClass: t3,
    exploreMore,
    className,
    scrollAmount: t4
  } = t0;
  const showArrows = t1 === undefined ? true : t1;
  const gap = t2 === undefined ? 16 : t2;
  const paddingClass = t3 === undefined ? "px-5 sm:px-8 lg:px-12" : t3;
  const scrollAmount = t4 === undefined ? 360 : t4;
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const el = scrollRef.current;
      if (!el) {
        return;
      }
      setCanScrollLeft(el.scrollLeft > 0);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };
    $[0] = t5;
  } else {
    t5 = $[0];
  }
  const updateScrollState = t5;
  let t6;
  let t7;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      const el_0 = scrollRef.current;
      if (!el_0) {
        return;
      }
      updateScrollState();
      el_0.addEventListener("scroll", updateScrollState, {
        passive: true
      });
      const resizeObserver = new ResizeObserver(updateScrollState);
      resizeObserver.observe(el_0);
      return () => {
        el_0.removeEventListener("scroll", updateScrollState);
        resizeObserver.disconnect();
      };
    };
    t7 = [updateScrollState];
    $[1] = t6;
    $[2] = t7;
  } else {
    t6 = $[1];
    t7 = $[2];
  }
  useEffect(t6, t7);
  let t8;
  if ($[3] !== scrollAmount) {
    t8 = direction => {
      const el_1 = scrollRef.current;
      if (!el_1) {
        return;
      }
      const amount = direction === "left" ? -scrollAmount : scrollAmount;
      el_1.scrollBy({
        left: amount,
        behavior: "smooth"
      });
    };
    $[3] = scrollAmount;
    $[4] = t8;
  } else {
    t8 = $[4];
  }
  const scroll = t8;
  let t9;
  if ($[5] !== className) {
    t9 = cn("relative", className);
    $[5] = className;
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  let t10;
  if ($[7] !== paddingClass) {
    t10 = cn("flex overflow-x-auto pb-4 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:h-1.5", paddingClass);
    $[7] = paddingClass;
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  const t11 = `${gap}px`;
  let t12;
  if ($[9] !== t11) {
    t12 = {
      gap: t11
    };
    $[9] = t11;
    $[10] = t12;
  } else {
    t12 = $[10];
  }
  let t13;
  if ($[11] !== exploreMore) {
    t13 = exploreMore && <Link href={exploreMore.href} className="flex w-[220px] shrink-0 cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 transition-colors hover:border-gray-400 hover:bg-white"><div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100"><ArrowRight className="h-5 w-5 text-gray-400" aria-hidden="true" /></div><p className="mt-3 text-sm font-black text-gray-600">{exploreMore.label}</p>{exploreMore.description && <p className="mt-1 text-xs text-gray-400">{exploreMore.description}</p>}</Link>;
    $[11] = exploreMore;
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  let t14;
  if ($[13] !== children || $[14] !== t10 || $[15] !== t12 || $[16] !== t13) {
    t14 = <div ref={scrollRef} className={t10} style={t12}>{children}{t13}</div>;
    $[13] = children;
    $[14] = t10;
    $[15] = t12;
    $[16] = t13;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== canScrollLeft || $[19] !== scroll || $[20] !== showArrows) {
    t15 = showArrows && canScrollLeft && <button onClick={() => scroll("left")} className="absolute left-2 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white/90 shadow-md backdrop-blur-sm transition-opacity hover:bg-white" aria-label="Scroll left"><ChevronLeft className="h-5 w-5 text-gray-700" /></button>;
    $[18] = canScrollLeft;
    $[19] = scroll;
    $[20] = showArrows;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] !== canScrollRight || $[23] !== scroll || $[24] !== showArrows) {
    t16 = showArrows && canScrollRight && <button onClick={() => scroll("right")} className="absolute right-2 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white/90 shadow-md backdrop-blur-sm transition-opacity hover:bg-white" aria-label="Scroll right"><ChevronRight className="h-5 w-5 text-gray-700" /></button>;
    $[22] = canScrollRight;
    $[23] = scroll;
    $[24] = showArrows;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== t14 || $[27] !== t15 || $[28] !== t16 || $[29] !== t9) {
    t17 = <div className={t9}>{t14}{t15}{t16}</div>;
    $[26] = t14;
    $[27] = t15;
    $[28] = t16;
    $[29] = t9;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  return t17;
}